import React from "react";
import Link from "next/link";
import { HoverableMention } from "@/components/HoverableMention";

export const parsePostText = (
  text: string,
  mentions: {
    id: string;
    name: string;
    avatar?: string | null;
    bio?: string | null;
    profile_picture?: { url: string } | null;
  }[] = [],
  tags: {
    id?: string;
    tag_id?: string;
    name?: string;
    tag_name?: string;
  }[] = []
): React.ReactNode[] => {
  // Match backend mentions, backend tags, plain hashtags, and HTTPS links
  const pattern =
    /(@\[(.*?)\]\(user:(.*?)\))|(#\[(.*?)\]\(tag:(.*?)\))|#([a-zA-Z0-9_][a-zA-Z0-9_-]*)\b|(https:\/\/[^\s]+)/g;

  const elements: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    const matchStart = match.index;

    if (lastIndex < matchStart) {
      elements.push(text.slice(lastIndex, matchStart));
    }

    // Backend mention
    if (match[1]) {
      const displayName = match[2];
      const userId = match[3];
      const mention = mentions.find((m) => m.id === userId);
      if (mention) {
        elements.push(
          <HoverableMention
            key={`mention-${userId}-${matchStart}`}
            user={{
              id: mention.id,
              name: mention.name,
              bio: mention.bio ?? undefined,
              avatar:
                mention.avatar || mention.profile_picture?.url || undefined,
            }}
          >
            <Link
              href={`/profile/${mention.name}`}
              className="text-blue-500 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              @{mention.name}
            </Link>
          </HoverableMention>
        );
      } else {
        elements.push(`@${displayName}`);
      }
    }
    // Backend tag
    else if (match[4]) {
      const tagName = match[5];
      const tagId = match[6];
      const tag = tags.find((t) => t.id === tagId || t.tag_id === tagId);
      const displayName = tag ? tag.name || tag.tag_name : tagName;
      elements.push(
        <Link
          key={`tag-${tagId}-${matchStart}`}
          href={`/tags/${displayName}`}
          className="text-blue-500 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          #{displayName}
        </Link>
      );
    }
    // Plain hashtag (not backend format)
    else if (match[7]) {
      const hashtag = match[7];
      elements.push(
        <Link
          key={`plain-tag-${hashtag}-${matchStart}`}
          href={`/tags/${encodeURIComponent(hashtag)}`}
          className="text-blue-500 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          #{hashtag}
        </Link>
      );
    }
    // HTTPS link
    else if (match[8]) {
      const url = match[8];
      elements.push(
        <a
          key={`link-${url}-${matchStart}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:underline break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {url}
        </a>
      );
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    elements.push(text.slice(lastIndex));
  }

  return elements;
};
